import { postReviewOfferFunnelStage } from "campaign-builder/checkout/billingApi";
import { ReviewOfferAnalyticsEvent } from "./useReviewOfferState";

/**
 * Review-offer funnel persistence (HUB-477).
 *
 * The six funnel events below have been captured to PostHog since HUB-453,
 * but PostHog is not readable by oppizi-api's sequence-trigger engine — so
 * "a trigger at every stage of the flow" was a *persistence* gap, not an
 * instrumentation one. This posts the same event, at the same call site, to
 * `POST /billing/checkout/review-offer/funnel-stage`.
 *
 * Deliberately NOT a new event stream: the wire value IS the PostHog event
 * name (`ReviewOfferAnalyticsEvent`), so the two surfaces cannot drift.
 *
 * Fire-and-forget by construction — returns void, never throws, never
 * awaited by the UI. A funnel row is never worth a spinner or an error state.
 */
export type ReviewOfferFunnelStage = Exclude<ReviewOfferAnalyticsEvent, "review_submit_unconfirmed">;

// PostHog-only events. The API's funnel ladder (ReviewOfferFunnelStages.ts in
// oppizi-api) rejects unknown stages with a 400, and an unconfirmed G2 submit
// is not progress on that ladder anyway.
const POSTHOG_ONLY_EVENTS: ReadonlySet<ReviewOfferAnalyticsEvent> = new Set(["review_submit_unconfirmed"]);

const isFunnelStage = (event: ReviewOfferAnalyticsEvent): event is ReviewOfferFunnelStage =>
  !POSTHOG_ONLY_EVENTS.has(event);

export const recordReviewOfferFunnelStage = (
  stage: ReviewOfferAnalyticsEvent,
  campaignId: string | number | undefined
): void => {
  if (!isFunnelStage(stage)) return;
  // The stage is campaign-scoped server-side (the sequence triggers it fires
  // carry a campaign scope), so a stage with no campaign has nowhere to land.
  if (campaignId === undefined || campaignId === null || campaignId === "") return;

  // `postReviewOfferFunnelStage` already swallows its own failures; the
  // second `.catch` is belt-and-braces so a future change there can never
  // turn this into an unhandled rejection in the checkout drawer.
  postReviewOfferFunnelStage({ stage, campaign_id: campaignId }).catch(() => {});
};
